/**
 * Measures a finished upload or download session and reports it, so managers can see
 * each person's real line speed on /transfers (2026-09-26). Transfers go straight
 * between the browser and Cloud Storage; only the browser knows how fast they were.
 *
 * Speed = bytes moved / the time at least one file was moving. Gaps where nothing ran
 * (the person away, the laptop asleep) are left out, so the number is the line's.
 */
import { api } from './api';

export interface TimedFile { size: number; startedAt?: number; finishedAt?: number }

interface NetInfo { effectiveType?: string; downlink?: number }

/** Total length of the union of the files' [start, finish] intervals, in seconds. */
export function busySeconds(files: TimedFile[]): number {
  const spans = files
    .filter((f) => f.startedAt && f.finishedAt && f.finishedAt > f.startedAt)
    .map((f) => [f.startedAt as number, f.finishedAt as number] as const)
    .sort((a, b) => a[0] - b[0]);
  let total = 0, curS = -1, curE = -1;
  for (const [s, e] of spans) {
    if (s > curE) { if (curE > curS) total += curE - curS; curS = s; curE = e; }
    else if (e > curE) curE = e;
  }
  if (curE > curS) total += curE - curS;
  return total / 1000;
}

export function reportTransfer(direction: 'upload' | 'download', files: TimedFile[], extra: { maxLanes?: number; mode?: string } = {}) {
  const moved = files.filter((f) => f.startedAt && f.finishedAt);
  const bytes = moved.reduce((n, f) => n + f.size, 0);
  const busy = busySeconds(moved);
  if (!moved.length || bytes < 5 * 1024 * 1024 || busy <= 0) return;
  let net: NetInfo = {};
  try { net = (navigator as unknown as { connection?: NetInfo }).connection ?? {}; } catch { /* not offered */ }
  const started = Math.min(...moved.map((f) => f.startedAt as number));
  const finished = Math.max(...moved.map((f) => f.finishedAt as number));
  void api.post('/transfers', {
    direction, files: moved.length, bytes, busy_seconds: Math.round(busy * 100) / 100,
    max_lanes: extra.maxLanes, mode: extra.mode,
    net_type: net.effectiveType, net_downlink_mbps: typeof net.downlink === 'number' ? net.downlink : undefined,
    started_at: new Date(started).toISOString(), finished_at: new Date(finished).toISOString(),
  }).catch(() => { /* a lost report never bothers the person */ });
}
